import React, {useState, useEffect} from "react"

import Button from "@irati/ui/src/Button"
import {Body} from "@irati/ui/src/Typography"
import mfaService from "@irati/auth/services/mfa"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import {mfaTracking} from "@irati-tracking/platform"
import {MfaSetup, MfaTotpUser} from "@irati/types"

import mfaTotpService from "@irati/auth/services/mfa_totp"
import Loading from "@irati/ui/src/Loading"
import Link from "@irati/ui/src/Link"
import Spacing from "@irati/ui/src/Spacing"
import {Modal} from "../../BaseModal"
import {ButtonContainer} from "../styled"
import {Screens, SelectScreen} from "../index"
import RequestBody, {MFAType, OnMfaChange} from "./body"

export interface MFAState {
  sms?: MfaSetup | null
  totp?: MfaTotpUser | null
}

interface Props extends InjectIntl {
  selectScreen: SelectScreen
  onCancel(): void
  show: boolean
}

const MFAPopupRequest: React.FC<Props> = (props) => {
  const [load, setLoad] = useState<Promise<any>>(Promise.resolve())
  const [mfa, setMfa] = useState<MFAState>({})
  const [selectedMethod, setSelectedMethod] = useState<MFAType>(MFAType.Sms)

  useEffect(() => {
    if (props.show) {
      mfaTracking.popupShown()
    }

    setLoad(
      Promise.all([mfaService.getUser(), mfaTotpService.getUser()]).then(([sms, totp]) => {
        if (sms?.isConfirmed) {
          setSelectedMethod(MFAType.Sms)
        } else if (totp?.isConfirmed) {
          setSelectedMethod(MFAType.Totp)
        }
        setMfa({sms, totp})
      })
    )
  }, [props.show])

  const handleSubmit = () => {
    switch (selectedMethod) {
      case MFAType.Sms:
        if (mfa.sms?.isConfirmed) {
          return props.selectScreen(Screens.SMS_ENTER)
        } else {
          return props.selectScreen(Screens.SMS_SETUP)
        }
      case MFAType.Totp:
        if (mfa.totp?.isConfirmed) {
          return props.selectScreen(Screens.TOTP_ENTER)
        } else {
          return props.selectScreen(Screens.TOTP_SETUP)
        }
    }
  }

  const handleCancel = () => {
    props.onCancel()
    mfaTracking.popupCancel()
  }

  const handleMfaChange: OnMfaChange = (val) => {
    setSelectedMethod(val)
  }

  const {show, formatMessage} = props
  return (
    <Modal show={show} header="platform.mfa.request.title">
      <Loading load={load}>
        <Body>
          <FormattedMessage
            id="platform.mfa.request.bodyWithUrl"
            values={{
              url: (
                <Link href={formatMessage({id: "platform.mfa.request.href"})}>
                  <FormattedMessage id="platform.mfa.request.hrefText" />
                </Link>
              )
            }}
          />
          <Spacing marginBottom={2} />
          <RequestBody mfa={mfa} selectedMethod={selectedMethod} onChange={handleMfaChange} />
        </Body>
        <ButtonContainer>
          <Button tertiary onClick={handleCancel} id="mfa_request_buttonCancel" marginRight={2}>
            <FormattedMessage id="platform.mfa.request.cancel" />
          </Button>
          <Button onClick={handleSubmit} primary>
            <FormattedMessage id="platform.mfa.request.continue" />
          </Button>
        </ButtonContainer>
      </Loading>
    </Modal>
  )
}

export default injectIntl(MFAPopupRequest)
